import React, { useState } from 'react';
import { Button, Dialog, Divider, Grid2, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm, SubmitHandler } from 'react-hook-form';
import { UserFormValues } from '../../user/models/user';
import { CloseIcon } from '../../shared/components/AppIcons';
import * as HttpStatus from 'http-status-codes';
import theme from '../../../core/theme';
import { useNavigate, useParams } from 'react-router';
import { resetPassword } from '../services/auth.services';
import { isEmpty } from 'lodash';

const useStyles = makeStyles({
  paper: {
    minWidth: '593px !important',
    minHeight: '137px !important',
    borderRadius: '21px',
    boxShadow: 'rgba(242, 242, 242, 1)',
    padding: '16px',
  },
  close: {
    fontSize: '35px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  text: {
    fontSize: '16px',
    '& .highlightedText': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  bold: {
    fontWeight: 'bolder !important',
  },
  button: {
    height: '38px',
    fontSize: '18px !important',
    fontFamily: 'Lato !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  cancel: {
    fontSize: '16px !important',
    color: '#F67570 !important',
    marginRight: '24px !important',
  },
});

const ResetPasswordModal: React.FC<{}> = () => {
  const classes = useStyles();
  const { token } = useParams();
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    register,
    formState: { errors },
    clearErrors,
    getValues,
    handleSubmit,
  } = useForm<UserFormValues>({ mode: 'onBlur' });

  const onSubmit: SubmitHandler<UserFormValues> = async (form) => {
    if (form.password) {
      try {
        const response = await resetPassword(token || '', form.password);
        if (response.token) {
          setShowAlert(false);
          navigate('/');
        } else {
          navigate('/login');
        }
      } catch (error) {
        if (error?.response?.status === HttpStatus.NOT_FOUND) {
          setErrorMessage('Not a valid token');
        } else {
          setErrorMessage('Please try again');
        }
      }
    } else {
      setErrorMessage('Please enter password');
    }
  };

  return (
    <Dialog
      open={showAlert}
      aria-modal="true"
      role="dialog"
      onClose={() => setShowAlert(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      {!isEmpty(errorMessage) && (
        <Grid2 container mb={2} justifyContent="center">
          <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
        </Grid2>
      )}

      <Grid2 container justifyContent="space-between">
        <Grid2 container className={classes.bold}>
          <span style={{ fontWeight: 'inherit' }}>Reset Password</span>
        </Grid2>
        <Grid2 container>
          <IconButton onClick={() => setShowAlert(false)} role="button" sx={{ padding: 0 }}>
            <CloseIcon viewBox="0 0 42 42" className={classes.close} />
          </IconButton>
        </Grid2>
      </Grid2>

      <Grid2 mb={1}>
        <Divider
          orientation="horizontal"
          flexItem
          sx={{
            backgroundColor: '#E1E3E8',
            paddingBottom: '1px',
          }}
        />
      </Grid2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid2 p={1}>
          <Grid2 container direction="column" mb={2}>
            <span>Enter your New Password to reset it</span>
          </Grid2>

          <Grid2 container mt={3} direction="column">
            <label htmlFor="inputPassword" className={classes.text}>
              New Password*
              <input
                {...register('password', {
                  required: 'Password is Required',
                })}
                name="password"
                id="inputPassword"
                className={`form-control ${errors.password && 'is-invalid'}`}
                placeholder="Enter your Password"
              />
              {errors.password && <span>{errors.password.message}</span>}
            </label>
          </Grid2>

          <Grid2 container mt={3} direction="column">
            <label htmlFor="inputConfirmPassword" className={classes.text}>
              Confirm Password*
              <input
                {...register('confirmPassword', {
                  required: 'Confirm Password is Required',
                  validate: (value) => value === getValues('password') || 'Passwords do not match',
                })}
                name="confirmPassword"
                id="inputConfirmPassword"
                className={`form-control ${errors.confirmPassword && !isEmpty(errorMessage) && 'is-invalid'}`}
                placeholder="Re-enter your Password"
                onChange={(e) => {
                  setErrorMessage('');
                  clearErrors('confirmPassword');
                  if (e.target.value !== getValues('password')) {
                    setErrorMessage('Passwords do not match');
                  }
                }}
              />
              {errors.confirmPassword && <span>{errors.confirmPassword.message}</span>}
            </label>
          </Grid2>
        </Grid2>

        <Grid2 container justifyContent="end" mt={3}>
          <Button variant="text" type="button" className={classes.cancel} onClick={() => setShowAlert(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={classes.button}
            disabled={!token || !isEmpty(errorMessage || errors.password || errors.confirmPassword)}
            style={{ width: 'auto', textTransform: 'none' }}
          >
            Reset Password
          </Button>
        </Grid2>
      </form>
    </Dialog>
  );
};

export default ResetPasswordModal;
